<template>
  <div>
    <pa-admin-toolbar
      :per-page="perPage"
      :search.sync="searchInput"
      :from.sync="fromInput"
      :to.sync="toInput"
      :busy="loading"
      placeholder="Search by keyword, email, user ID or run ID"
      @per-page="setPerPage"
      @search="applySearch"
    >
      <button type="button" class="paa-btn paa-btn--secondary" :disabled="loading || !total" @click="exportRows">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" /></svg>
        Export All
      </button>
    </pa-admin-toolbar>

    <div class="paa-filters">
      <pa-admin-filter label="Provider" :value="filters.provider" :options="providerOptions" @input="setFilter('provider', $event)" />
      <pa-admin-filter label="Page score" :value="filters.band" :options="bandOptions" @input="setFilter('band', $event)" />
      <pa-admin-filter label="Scenario" :value="filters.scenario" :options="scenarioOptions" @input="setFilter('scenario', $event)" />
      <pa-admin-filter label="Show" :value="filters.review" :options="reviewOptions" @input="setFilter('review', $event)" />
      <pa-admin-filter label="Verdict" :value="filters.verdict" :options="verdictOptions" @input="setFilter('verdict', $event)" />
    </div>

    <div v-if="loadError" class="paa-error">{{ loadError }}</div>

    <div class="sv-tiles">
      <div v-for="tile in tiles" :key="tile.key" class="sv-tile">
        <b>{{ tile.value }}</b>
        <sv-tip :label="tile.label" :lines="tileTips[tile.key]" />
      </div>
    </div>

    <div class="paa-stat-row">
      <span class="paa-spacer"></span>
      <b>{{ total }} rows</b>
    </div>

    <div class="paa-card paa-tblwrap">
      <table class="paa-tbl">
        <thead>
          <tr>
            <th :class="thClass('createdAt')" @click="sortBy('createdAt')">{{ sortMark('createdAt') }}<sv-tip label="Date (UTC)" :lines="headTips.date" /></th>
            <th :class="thClass('keyword')" @click="sortBy('keyword')">{{ sortMark('keyword') }}<sv-tip label="Keyword" :lines="headTips.keyword" /></th>
            <th><sv-tip label="User" :lines="headTips.user" /></th>
            <th><sv-tip label="Provider" :lines="headTips.provider" /></th>
            <th :class="thClass('page1Score', 'paa-r')" @click="sortBy('page1Score')">{{ sortMark('page1Score') }}<sv-tip label="Page 1" :lines="headTips.page1" /></th>
            <th :class="thClass('page2Score', 'paa-r')" @click="sortBy('page2Score')">{{ sortMark('page2Score') }}<sv-tip label="Page 2" :lines="headTips.page2" /></th>
            <th :class="thClass('relatedScore', 'paa-r')" @click="sortBy('relatedScore')">{{ sortMark('relatedScore') }}<sv-tip label="Related" :lines="headTips.related" /></th>
            <th><sv-tip label="Other provider" :lines="headTips.other" /></th>
            <th><sv-tip label="Local" :lines="headTips.local" /></th>
            <th :class="thClass('scenario')" @click="sortBy('scenario')">{{ sortMark('scenario') }}<sv-tip label="Decision" :lines="headTips.decision" /></th>
            <th :class="thClass('competitorCount', 'paa-r')" @click="sortBy('competitorCount')">{{ sortMark('competitorCount') }}<sv-tip label="Comp." heading="Competitors" :lines="headTips.comp" /></th>
            <th><sv-tip label="Verdict" :lines="headTips.verdict" /></th>
            <th><sv-tip label="Details" :lines="headTips.details" /></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in records">
            <tr :key="row.id">
              <td>{{ row.createdAt }}</td>
              <td>
                {{ row.keyword }}
                <span class="paa-sub">{{ row.location }}{{ row.language ? ' · ' + row.language : '' }} · {{ row.page1Rule }} rule</span>
              </td>
              <td>
                <a v-if="row.userEmail" :href="userAccountsHref(row.userEmail)" target="_blank" rel="noopener">{{ row.userEmail }}</a>
                <span v-else>{{ row.userId || '' }}</span>
                <span v-if="row.pageRunId" class="paa-sub">
                  run <a v-if="runHref(row)" :href="runHref(row)" target="_blank" rel="noopener">{{ row.pageRunId }}</a>
                  <template v-else>{{ row.pageRunId }}</template>
                </span>
              </td>
              <td>{{ provider(row.primaryProvider) }}</td>
              <td class="paa-r"><span :class="scoreClass(row.page1Band)">{{ score(row.page1Score) }}</span></td>
              <td class="paa-r"><span :class="scoreClass(row.page2Band)">{{ score(row.page2Score) }}</span></td>
              <td class="paa-r">{{ score(row.relatedScore) }}</td>
              <td>
                <template v-if="row.otherProvider">
                  {{ provider(row.otherProvider) }}
                  <span :class="scoreClass(row.otherPage1Band)">{{ score(row.otherPage1Score) }}</span>
                  <span :class="scoreClass(row.otherPage2Band)">{{ score(row.otherPage2Score) }}</span>
                </template>
              </td>
              <td>
                <template v-if="row.localizationAction && row.localizationAction !== 'skipped'">
                  <span :class="['sv-score', row.localizationOutsideShare > 0.5 ? 'sv-score--fail' : 'sv-score--ok']">{{ Math.round((row.localizationOutsideShare || 0) * 100) }}% outside</span>
                  <span class="paa-sub">{{ localizationText(row) }}</span>
                </template>
                <span v-else-if="row.localizationReason && row.localizationReason !== 'mode'" class="paa-muted">{{ localizationReason(row.localizationReason) }}</span>
              </td>
              <td>
                <sv-tip :heading="'Scenario ' + (row.scenario || '?')" :lines="scenarioTips[row.scenario] || []">
                  <pa-admin-badge :badge="scenarioBadge(row)" />
                </sv-tip>
                <span class="paa-sub">{{ decisionText(row) }}</span>
              </td>
              <td class="paa-r">{{ row.competitorCount === null || row.competitorCount === undefined ? '' : row.competitorCount }}</td>
              <td><pa-admin-badge v-if="row.manualVerdict" :badge="verdictBadge(row.manualVerdict)" /><span v-else-if="row.needsReview || row.sampled" class="paa-muted">to review</span></td>
              <td class="paa-r">
                <button type="button" class="paa-btn paa-btn--ghost paa-btn--sm" :disabled="!row.hasRows && expandedId !== row.id" @click="toggleDetail(row)">
                  {{ expandedId === row.id ? 'Close' : (row.hasRows ? 'Details' : 'No rows kept') }}
                </button>
              </td>
            </tr>
            <tr v-if="expandedId === row.id" :key="row.id + '-detail'" class="sv-detail">
              <td colspan="13">
                <div v-if="detailLoading" class="paa-muted">Loading…</div>
                <div v-else-if="detailError" class="paa-error">{{ detailError }}</div>
                <template v-else-if="detail">
                  <div class="paa-muted" style="margin-bottom: 6px">
                    Both providers' pages as Google returned them. Greyed rows are the ones that do not mention the keyword; the score is the share of rows that do.
                  </div>
                  <div class="sv-detail-grid">
                    <div v-for="side in detailSides" :key="side.key">
                      <b>{{ side.key === 'localized' ? 'Localized search ("' + (detail.localizedKeyword || '') + '") on ' + provider(side.data.provider) : provider(side.data.provider) }}</b>
                      <span class="paa-muted"> — page 1 {{ score(side.data.page1.score) }} ({{ side.data.page1.band }}, {{ side.data.page1.matched }} of {{ side.data.page1.rows }} rows match), page 2 {{ score(side.data.page2.score) }} ({{ side.data.page2.band }})</span>
                      <span v-if="side.data.related !== null && side.data.related !== undefined" class="paa-muted"> · related searches {{ score(side.data.related) }}</span>
                      <div v-for="page in ['page1', 'page2']" :key="page">
                        <div class="paa-muted" style="margin-top: 8px">Page {{ page === 'page1' ? 1 : 2 }}</div>
                        <ol class="sv-rows">
                          <li v-for="(r, i) in side.data.rows[page]" :key="i" :class="{ 'sv-row--miss': !rowMatches(side.data, r) }">
                            <a :href="r.url" target="_blank" rel="noopener">{{ r.title || r.url }}</a>
                            <span class="paa-muted"> — {{ r.description }}</span>
                          </li>
                          <li v-if="!side.data.rows[page] || !side.data.rows[page].length" class="paa-muted">no rows</li>
                        </ol>
                      </div>
                      <div v-if="side.data.related_searches && side.data.related_searches.length" class="paa-muted" style="margin-top: 6px">
                        Related searches: {{ side.data.related_searches.join(', ') }}
                      </div>
                    </div>
                  </div>
                  <div v-if="detail.localizationAction && detail.localizationAction !== 'skipped'" style="margin-top: 10px">
                    <b>Localization</b>
                    <div class="paa-muted">
                      {{ Math.round((detail.localizationOutsideShare || 0) * 100) }}% of page 1 targets another place{{ detail.localizationAiConfirmed === true ? ', confirmed by AI' : detail.localizationAiConfirmed === false ? ', not confirmed by AI' : '' }}.
                      {{ localizationText(detail) }}{{ detail.localizedOutsideShare !== null && detail.localizedOutsideShare !== undefined ? ' Second search: ' + Math.round(detail.localizedOutsideShare * 100) + '% outside, page 1 ' + score(detail.localizedPage1Score) + ' (' + detail.localizedPage1Band + ').' : '' }}
                    </div>
                  </div>
                  <div v-if="detail.aiResults && detail.aiResults.length" style="margin-top: 10px">
                    <b>AI relevance check</b>
                    <div v-for="(ai, i) in detail.aiResults" :key="i" class="paa-muted">
                      {{ provider(ai.provider) }}: {{ ai.relevant ? 'relevant' : 'not relevant' }}{{ ai.relevance_score !== null && ai.relevance_score !== undefined ? ' (' + ai.relevance_score + '/100' + (ai.confidence ? ', ' + ai.confidence : '') + ')' : '' }}{{ ai.reason ? ' — ' + ai.reason : '' }}{{ ai.error ? ' — ' + ai.error : '' }}
                    </div>
                  </div>
                  <div class="sv-verdict">
                    <span>Manual verdict:</span>
                    <button type="button" class="paa-btn paa-btn--sm" :class="detail.manualVerdict === 'healthy' ? 'paa-btn--primary' : 'paa-btn--secondary'" :disabled="verdictSaving" @click="saveVerdict(row, 'healthy')">Healthy</button>
                    <button type="button" class="paa-btn paa-btn--sm" :class="detail.manualVerdict === 'poisoned' ? 'paa-btn--primary' : 'paa-btn--secondary'" :disabled="verdictSaving" @click="saveVerdict(row, 'poisoned')">Poisoned</button>
                    <button v-if="detail.manualVerdict" type="button" class="paa-btn paa-btn--ghost paa-btn--sm" :disabled="verdictSaving" @click="saveVerdict(row, '')">Clear</button>
                    <span v-if="detail.manualVerdictAt" class="paa-muted">set {{ detail.manualVerdictAt }}</span>
                  </div>
                </template>
              </td>
            </tr>
          </template>
          <tr v-if="!records.length">
            <td class="paa-empty" colspan="13">{{ loading ? 'Loading…' : 'No checked SERP matches this search.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pa-admin-pager :page="page" :page-count="pageCount" @change="setPage" />
  </div>
</template>

<script>
import PaAdminBadge from '../PopAuthority/PaAdminBadge.vue'
import PaAdminFilter from '../PopAuthority/PaAdminFilter.vue'
import PaAdminPager from '../PopAuthority/PaAdminPager.vue'
import PaAdminToolbar from '../PopAuthority/PaAdminToolbar.vue'
import SvTip from './SvTip.vue'
import paAdminListMixin from '../PopAuthority/paAdminListMixin'
import {
  SV_BAND_OPTIONS,
  SV_BAND_TONES,
  SV_DECISION_LABELS,
  SV_HEAD_TIPS,
  SV_LOCALIZATION_LABELS,
  SV_LOCALIZATION_REASONS,
  SV_PAGE2_LABELS,
  SV_PROVIDER_OPTIONS,
  SV_REVIEW_OPTIONS,
  SV_SCENARIO_OPTIONS,
  SV_SCENARIO_TIPS,
  SV_TILE_TIPS,
  SV_VERDICT_OPTIONS,
  svMoney,
  svProvider,
  svScore,
} from './serpValidationConstants'

const SCENARIO_TONES = { A: 'suc', B: 'info', C: 'warn', D: 'dan' }

/**
 * History / Review Queue of the SERP validation log. One row per checked competitor fetch; the
 * detail shows both providers' rows (the ones that failed the rule greyed out), the AI result and
 * the manual verdict. `review` presets the "Show" filter ('all' or 'queue').
 */
export default {
  name: 'AdminSerpValidationHistory',
  components: { PaAdminBadge, PaAdminFilter, PaAdminPager, PaAdminToolbar, SvTip },
  mixins: [paAdminListMixin],
  paAdmin: { action: 'getSerpValidationRecordsForAdmin', sort: 'createdAt', tab: 'serp-validation' },
  props: {
    review: { type: String, default: 'all' },
  },
  data () {
    return {
      filters: { band: 'all', provider: 'all', scenario: 'all', review: this.review, verdict: 'all' },
      mode: '',
      expandedId: null,
      detail: null,
      detailLoading: false,
      detailError: '',
      verdictSaving: false,
      bandOptions: SV_BAND_OPTIONS,
      providerOptions: SV_PROVIDER_OPTIONS,
      scenarioOptions: SV_SCENARIO_OPTIONS,
      reviewOptions: SV_REVIEW_OPTIONS,
      verdictOptions: SV_VERDICT_OPTIONS,
      headTips: SV_HEAD_TIPS,
      tileTips: SV_TILE_TIPS,
      scenarioTips: SV_SCENARIO_TIPS,
    }
  },
  computed: {
    tiles () {
      const stats = this.stats || {}
      const cost = stats.extraCost ? ' · ' + this.money(stats.extraCost) : ''
      return [
        { key: 'checked', label: 'SERPs checked', value: stats.checked || 0 },
        { key: 'page1Fail', label: 'page 1 poisoned', value: stats.page1Fail || 0 },
        { key: 'page2Fail', label: 'page 2 poisoned', value: stats.page2Fail || 0 },
        { key: 'borderline', label: 'borderline', value: stats.borderline || 0 },
        { key: 'switched', label: 'switched provider', value: stats.switched || 0 },
        { key: 'page2Replaced', label: 'page 2 replaced', value: stats.page2Replaced || 0 },
        { key: 'uncertain', label: 'uncertain', value: stats.uncertain || 0 },
        { key: 'aiCalls', label: 'AI calls', value: stats.aiCalls || 0 },
        { key: 'extraCalls', label: 'extra provider calls' + cost, value: stats.extraCalls || 0 },
        { key: 'shortLists', label: 'fewer than 10 competitors', value: stats.shortLists || 0 },
        { key: 'reviewQueue', label: 'waiting for a verdict', value: stats.reviewQueue || 0 },
        { key: 'verdicts', label: 'verdicts poisoned / healthy', value: (stats.verdictPoisoned || 0) + ' / ' + (stats.verdictHealthy || 0) },
        { key: 'localized', label: 'localized searches', value: stats.localized || 0 },
      ]
    },
    detailSides () {
      const rows = (this.detail && this.detail.rows) || {}
      return ['primary', 'other', 'localized'].filter((key) => rows[key]).map((key) => ({ key, data: rows[key] }))
    },
  },
  methods: {
    provider: svProvider,
    score: svScore,
    money: svMoney,
    afterLoad (data) {
      this.mode = data.mode || ''
      if (this.expandedId && !this.records.some((row) => row.id === this.expandedId)) this.expandedId = null
    },
    scoreClass (band) {
      return ['sv-score', 'sv-score--' + (band || 'empty')]
    },
    scenarioBadge (row) {
      return { label: 'Scenario ' + (row.scenario || '?'), tone: SCENARIO_TONES[row.scenario] || 'grey' }
    },
    verdictBadge (verdict) {
      return { label: verdict === 'poisoned' ? 'Poisoned' : 'Healthy', tone: verdict === 'poisoned' ? 'dan' : 'suc' }
    },
    localizationText (row) {
      const label = SV_LOCALIZATION_LABELS[row.localizationAction] || row.localizationAction || ''
      const reason = row.localizationAction === 'local_failed' ? SV_LOCALIZATION_REASONS[row.localizationReason] : ''
      return reason ? label + ' (' + reason + ')' : label
    },
    localizationReason (reason) {
      return SV_LOCALIZATION_REASONS[reason] || reason || ''
    },
    decisionText (row) {
      const parts = [SV_DECISION_LABELS[row.decision] || row.decision || '']
      if (SV_PAGE2_LABELS[row.page2Action]) parts.push(SV_PAGE2_LABELS[row.page2Action])
      if (row.aiCalls) parts.push(row.aiCalls + ' AI call' + (row.aiCalls > 1 ? 's' : ''))
      return parts.filter(Boolean).join(', ')
    },
    runHref (row) {
      if (!row.siteId || !row.pageId || !row.pageRunId) return ''
      return this.$router.resolve({
        name: 'PageRun',
        params: { siteId: row.siteId, pageId: row.pageId, pageRunId: row.pageRunId },
      }).href
    },
    rowMatches (side, r) {
      // the helper counts a row when its words start with the keyword prefixes; mirrored here for the greying only
      const text = [r.title, r.url, r.description].join(' ').toLowerCase()
      const words = text.match(/[^\W_]+/g) || []
      const prefixes = (side.page1.terms || []).map((t) => (t.length > 5 ? t.slice(0, 5) : t.replace(/s$/, '')))
      const cityPrefixes = (side.page1.city || []).map((t) => (t.length > 5 ? t.slice(0, 5) : t.replace(/s$/, '')))
      const hits = prefixes.filter((p) => words.some((w) => w.startsWith(p))).length
      const cityHits = cityPrefixes.filter((p) => words.some((w) => w.startsWith(p))).length
      if (side.page1.rule === 'local') return hits >= 1 && cityHits >= 1
      if (side.page1.rule === 'single') return prefixes.length === 0 || hits >= 1
      return hits >= 2
    },
    toggleDetail (row) {
      if (this.expandedId === row.id) {
        this.expandedId = null
        this.detail = null
        return
      }
      this.expandedId = row.id
      this.detail = null
      this.detailError = ''
      this.detailLoading = true
      this.$store.dispatch('getSerpValidationRecordForAdmin', { recordId: row.id })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'load failed')
          this.detail = data.record
        })
        .catch((error) => {
          this.detailError = this.errorText(error, 'We could not load this record. Try again in a moment.')
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    saveVerdict (row, verdict) {
      this.verdictSaving = true
      this.$store.dispatch('setSerpValidationVerdictForAdmin', { recordId: row.id, body: { verdict } })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'save failed')
          this.detail = data.record
          row.manualVerdict = data.record.manualVerdict
          row.manualVerdictAt = data.record.manualVerdictAt
        })
        .catch((error) => {
          this.notifyError(this.errorText(error, 'The verdict was not saved. Try again in a moment.'))
        })
        .finally(() => {
          this.verdictSaving = false
        })
    },
    exportRows () {
      const header = [
        'Date (UTC)', 'Keyword', 'Location', 'Language', 'Rule', 'User ID', 'Email', 'Page run', 'Mode',
        'Provider', 'Page 1 score', 'Page 1 band', 'Page 2 score', 'Page 2 band', 'Related searches score',
        'Other provider', 'Other page 1 score', 'Other page 1 band', 'Other page 2 score', 'Other page 2 band',
        'Scenario', 'Decision', 'Page 2 action', 'Provider used', 'AI calls', 'AI relevant', 'Extra calls',
        'Extra cost ($)', 'Competitors', 'Needs review', 'Sampled', 'Verdict', 'Verdict at',
        'Localization', 'Outside share', 'Localization AI', 'Localized keyword', 'Localized page 1 score',
        'Localized page 1 band', 'Localized outside share',
      ]
      return this.exportCsv(header, (row) => [
        row.createdAt, row.keyword, row.location || '', row.language || '', row.page1Rule || '', row.userId || '',
        row.userEmail || '', row.pageRunId || '', row.mode || '', row.primaryProvider || '',
        row.page1Score, row.page1Band || '', row.page2Score, row.page2Band || '', row.relatedScore,
        row.otherProvider || '', row.otherPage1Score, row.otherPage1Band || '', row.otherPage2Score,
        row.otherPage2Band || '', row.scenario || '', row.decision || '', row.page2Action || '',
        row.providerUsed || '', row.aiCalls || 0, row.aiRelevant === null || row.aiRelevant === undefined ? '' : row.aiRelevant,
        row.extraCalls || 0, row.extraCost || 0, row.competitorCount, row.needsReview ? 'yes' : '',
        row.sampled ? 'yes' : '', row.manualVerdict || '', row.manualVerdictAt || '',
        row.localizationAction || '', row.localizationOutsideShare, row.localizationAiConfirmed === null || row.localizationAiConfirmed === undefined ? '' : row.localizationAiConfirmed,
        row.localizedKeyword || '', row.localizedPage1Score, row.localizedPage1Band || '', row.localizedOutsideShare,
      ])
    },
  },
}
</script>
